/**
 * Which door someone signed in through — and therefore where they belong.
 *
 * The rule, in the owner's words: sign in through the Partner Portal and you land in your
 * workspace with your clients; sign in as a client and you land on the client dashboard, or
 * wherever you left off.
 *
 * WHY THIS IS PERSISTED RATHER THAN JUST HELD IN STATE. The partner flag used to live only in
 * React state, and two things knocked it over:
 *
 *   1. `closeAccountModal()` deliberately clears it, and the sign-in handler calls that BEFORE it
 *      reads it. The in-flight handler kept its own copy, so that was thought safe — but setting
 *      the session also triggers the auto-resume effect, which starts a SECOND handler with a
 *      fresh copy where the flag is already gone. That one takes the consumer path and overwrites
 *      the workspace the first was heading to.
 *   2. A page reload starts with no flag at all, so a partner who refreshed was posted back to the
 *      client fact-find.
 *
 * Session-scoped (not local) storage: it should last as long as the tab and no longer, so a shared
 * machine never inherits someone else's door.
 */
export type SignInDoor = 'partner' | 'client';

const KEY = 'acw:signInDoor';

/** Records the door. Called at the moment the choice is made, not when sign-in completes. */
export function setSignInDoor(door: SignInDoor): void {
    try {
        globalThis.sessionStorage.setItem(KEY, door);
    } catch {
        /* Private browsing or blocked storage — the in-memory flag still covers the common path. */
    }
}

/** The door this tab came through, or null when it is not known. */
export function getSignInDoor(): SignInDoor | null {
    try {
        const stored = globalThis.sessionStorage.getItem(KEY);
        return stored === 'partner' || stored === 'client' ? stored : null;
    } catch {
        return null;
    }
}

/** Forgotten on sign-out: the next person at this browser chooses their own door. */
export function clearSignInDoor(): void {
    try {
        globalThis.sessionStorage.removeItem(KEY);
    } catch {
        /* ignore */
    }
}
